import { useCallback, useEffect, useMemo, useState } from "react";

import type { GaConnection } from "@similarweb/ga-connect";
import { useActiveConnectionsList, useAddAccount } from "@similarweb/ga-connect";
import Sentry from "@similarweb/sentry-logger";

import { swSettings } from "app/common/services/swSettings";
import { usePersistentState } from "app/pages/digital-marketing/keywords-campaigns/shared/hooks";

const GSC_CONNECT_ERROR_POPUP_CLOSED = "popup_closed";

const getGscConnectErrorType = (callbackType: string | undefined, error: unknown): string | undefined => {
    if (callbackType) {
        return callbackType;
    }
    if (
        error !== null &&
        typeof error === "object" &&
        "type" in error &&
        typeof (error as { type: unknown }).type === "string"
    ) {
        return (error as { type: string }).type;
    }
    return undefined;
};

type UseGscConnectionReturn = {
    connect: () => void;
    connections: GaConnection[] | undefined;
    currentConnection: GaConnection | null;
    setCurrentConnection: (email: string) => void;
    isLoadingConnections: boolean;
    isConnecting: boolean;
};

/**
 * Hook for managing GSC account connection.
 *
 * Features:
 * - Provides `connect` function to trigger OAuth popup
 * - Tracks active GSC connections
 * - Persists selected connection email in localStorage (per user)
 * - Derives `currentConnection` from the connections list using stored or current user email
 * - Normalizes the persisted email to the fallback connection when needed
 * - Automatically clears stored email if no connections are available (all access revoked)
 */
export const useGscConnection = (): UseGscConnectionReturn => {
    const currentUserEmail = swSettings?.user?.username as string | undefined;
    const [connectionEmail, setConnectionEmail, clearConnectionEmail] = usePersistentState(
        `GSC_CONNECTION_EMAIL_${swSettings.user.id}`,
        currentUserEmail,
    );

    const { data: connections, isLoading: isLoadingConnections } = useActiveConnectionsList({
        onError: (error) => {
            const e = error instanceof Error ? error : new Error(String(error));
            Sentry.errorTracker({
                e,
                level: "error",
                message: "Error on fetching active GSC connections",
            });
        },
    });

    const [isConnecting, setIsConnecting] = useState(false);
    const { requestCodeAndCreateConnection } = useAddAccount({
        onSuccess: (data) => {
            // On reconnection of an active account, the API returns `googleAccountId` instead
            // of `ownerEmail`. This property is absent from the upstream type definition.
            const response = data as { ownerEmail?: string; googleAccountId?: string; connectionName: string };
            setConnectionEmail(response.ownerEmail || response.googleAccountId || response.connectionName);
            setIsConnecting(false);
        },
        onError: (error, type) => {
            setIsConnecting(false);
            const resolvedType = getGscConnectErrorType(type, error);
            if (resolvedType === GSC_CONNECT_ERROR_POPUP_CLOSED) {
                return;
            }
            const e = error instanceof Error ? error : new Error(String(error));
            Sentry.errorTracker({
                e,
                level: "error",
                message: "Error on connecting GSC account",
                params: {
                    extra: {
                        type: resolvedType,
                    },
                },
            });
        },
    });

    // Derive currentConnection from connections list
    const currentConnection = useMemo((): GaConnection | null => {
        if (!connections) {
            return null;
        }
        return (
            connections.find((c) => c.ownerEmail === connectionEmail) ||
            connections.at(0) || // fallback to use any connection so the user isn't blocked from using GSC
            null
        );
    }, [connections, connectionEmail]);

    // Persist connection email to reuse the same connection between visits
    useEffect(() => {
        if (
            connections &&
            !isLoadingConnections &&
            currentConnection &&
            currentConnection.ownerEmail !== connectionEmail
        ) {
            setConnectionEmail(currentConnection.ownerEmail);
        }
    }, [connections, isLoadingConnections, currentConnection, connectionEmail, setConnectionEmail]);

    // Clear stored email when no connections are available (all access revoked)
    useEffect(() => {
        if (connections && !isLoadingConnections && currentConnection === null) {
            clearConnectionEmail();
        }
    }, [connections, isLoadingConnections, currentConnection, clearConnectionEmail]);

    const connect = useCallback(() => {
        setIsConnecting(true);
        requestCodeAndCreateConnection();
    }, [requestCodeAndCreateConnection]);

    return {
        connect,
        connections: connections ?? undefined,
        currentConnection,
        setCurrentConnection: setConnectionEmail,
        isLoadingConnections,
        isConnecting,
    };
};
